// Inventory Engine Phase 1 — small pure helpers for the admin page.
//
// Quantities travel to and from the API as STRINGS (the backend stores
// Decimal128 and never float-parses); this module never converts them to
// JS numbers for arithmetic — the server does every conversion.

export const OPERATION_KEY_PREFIX = "inv-";

// One idempotency key per form submission attempt. Re-submitting the SAME
// form (double click, network retry) reuses it, so stock can never change
// twice; a fresh key is minted only after a confirmed success.
export function newOperationKey() {
  const c = (typeof globalThis !== "undefined" && globalThis.crypto) || null;
  if (c && typeof c.randomUUID === "function") {
    return OPERATION_KEY_PREFIX + c.randomUUID().replace(/-/g, "");
  }
  if (c && typeof c.getRandomValues === "function") {
    const bytes = c.getRandomValues(new Uint8Array(16));
    return OPERATION_KEY_PREFIX + Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
  }
  throw new Error("No secure random source available for an inventory operation key.");
}

export function inventoryErrorMessage(err, fallback = "Request failed") {
  const detail = err?.response?.data?.detail;
  if (!detail) return err?.message || fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((d) => d?.msg).filter(Boolean).join("; ") || fallback;
  return detail.message || detail.code || fallback;
}

// "1 PL = 20 CS = 240 PK = 1,440 EA" from a convert/stock response.
export function formatEquivalents(equivalents) {
  if (!Array.isArray(equivalents) || equivalents.length === 0) return "";
  return [...equivalents]
    .reverse()
    .map((e) => `${e.qty} ${e.uom_code}`)
    .join(" = ");
}

export function levelLabel(level) {
  if (!level) return "";
  if (level.is_base) return `${level.uom_code} (base)`;
  return `${level.label || level.uom_code} — ${level.uom_code} = ${level.factor_to_base} base`;
}

// ---- Phase 2: goods inward & lots ----------------------------------------------

// Drop empty-string / null / undefined fields so an untouched input is
// "not provided" (the server then applies its default) rather than "".
export function compactPayload(obj) {
  const out = {};
  Object.entries(obj || {}).forEach(([k, v]) => {
    if (v === "" || v === null || v === undefined) return;
    out[k] = typeof v === "string" ? v.trim() : v;
  });
  return out;
}

// "2026-10-05" in the viewer's own calendar (receipt dates are calendar dates).
export function todayISO(now = new Date()) {
  const p = (n) => String(n).padStart(2, "0");
  return `${now.getFullYear()}-${p(now.getMonth() + 1)}-${p(now.getDate())}`;
}

export function receiptStatusVariant(status) {
  if (status === "POSTED") return "secondary";
  if (status === "DRAFT") return "default";
  return "outline";
}

// "Expires 2026-12-31" / "Best before 2026-11-01" / "No date".
export function lotDateLabel(lot) {
  if (!lot) return "";
  if (lot.expiry_date) return `Expires ${lot.expiry_date}`;
  if (lot.best_before_date) return `Best before ${lot.best_before_date}`;
  return "No date";
}

// Short receiving summary for a GRN line: "10 BX → 100 EA accepted".
export function lineSummary(line) {
  if (!line) return "";
  const base = line.base || line.preview?.base;
  const baseUom = line.base_uom || line.preview?.base_uom || "";
  const unit = line.uom_code || line.preview?.uom_code || "";
  if (!base) return `${line.received_qty} ${unit} received`;
  return `${line.accepted_qty} ${unit} → ${base.accepted} ${baseUom} accepted`;
}
